import {
    COLORS,
    POSTER_BACKGROUNDS,
    TEXT_COLORS,
    contrastColor,
    fontPair,
    matchColor,
    presetEntry,
    sameColor,
} from '@/components/Editor/Shared/designTokens';
import { CIRCLE_SHAPE_ID } from '@/components/Editor/Shared/shapes';

// Background and sky colours; `null` keeps the theme artwork.
export const STAR_MAP_BACKGROUNDS = [
    { id: 0, name: 'Téma szerint', labelKey: 'star_map.background.by_theme', color: null },
    ...POSTER_BACKGROUNDS,
    { id: 11, name: 'Éjkék', labelKey: 'star_map.background.midnight', color: COLORS.midnight },
    { id: 12, name: 'Arany', labelKey: 'star_map.background.gold', color: COLORS.gold },
];

// A look is a theme artwork plus optional COLORS keys; omitted colours follow the theme.
// `sky` defaults to `background`, which makes the sky disc blend into the poster. `fonts` is a FONT_PAIRS key.
// `shape` is a SHAPES id (the classic disc when omitted).
const PRESET_LOOKS = [
    { name: 'Klasszikus fehér', labelKey: 'star_map.preset.classic_white', theme: 2, fonts: 'classic' },
    {
        name: 'Éjféli kék', labelKey: 'star_map.preset.midnight_blue',
        theme: 1,
        shape: 1,
        colors: { background: 'midnight', stars: 'white', lines: 'gold', text: 'white' },
        fonts: 'elegant',
    },
    { name: 'Arany csillagpor', labelKey: 'star_map.preset.gold_stardust', theme: 1, colors: { stars: 'gold', text: 'gold' }, fonts: 'garamond' },
    { name: 'Arany hold', labelKey: 'star_map.preset.gold_moon', theme: 4, fonts: 'bold' },
    {
        name: 'Krém és szépia', labelKey: 'star_map.preset.cream_sepia',
        theme: 2,
        colors: { background: 'cream', stars: 'sepia', text: 'sepia' },
        fonts: 'vintage',
    },
    {
        name: 'Púder álom', labelKey: 'star_map.preset.powder_dream',
        theme: 2,
        colors: { background: 'powder', sky: 'burgundy', stars: 'cream', text: 'burgundy' },
        fonts: 'romantic',
    },
    {
        name: 'Erdei éj', labelKey: 'star_map.preset.forest_night',
        theme: 1,
        colors: { background: 'forest', stars: 'cream', lines: 'gold', text: 'cream' },
        fonts: 'literary',
    },
    { name: 'Vízfesték éj', labelKey: 'star_map.preset.watercolour_night', theme: 3, fonts: 'artDeco' },
    {
        name: 'Bordó bársony', labelKey: 'star_map.preset.burgundy_velvet',
        theme: 1,
        colors: { background: 'burgundy', stars: 'cream', lines: 'gold', text: 'cream' },
        fonts: 'velvet',
    },
    { name: 'Minimál kör', labelKey: 'star_map.preset.minimal_circle', theme: 7, fonts: 'minimal' },
    {
        name: 'Északi fény', labelKey: 'star_map.preset.northern_lights',
        theme: 1,
        colors: { background: 'midnight', sky: 'blue', stars: 'white', lines: 'cream', text: 'white' },
        fonts: 'nordic',
    },
    {
        name: 'Terrakotta este', labelKey: 'star_map.preset.terracotta_evening',
        theme: 2,
        colors: { background: 'lightGrey', sky: 'terracotta', stars: 'cream', lines: 'cream', text: 'sepia' },
        fonts: 'warmSerif',
    },
    { name: 'Kék lagúna', labelKey: 'star_map.preset.blue_lagoon', theme: 8, fonts: 'soft' },
    {
        name: 'Fekete arany', labelKey: 'star_map.preset.black_gold',
        theme: 1,
        colors: { background: 'black', stars: 'gold', lines: 'gold', text: 'gold' },
        fonts: 'display',
    },
    {
        name: 'Zsályakert', labelKey: 'star_map.preset.sage_garden',
        theme: 2,
        colors: { background: 'sage', stars: 'forest', lines: 'forest', text: 'forest' },
        fonts: 'editorial',
    },
    {
        name: 'Kézírásos emlék', labelKey: 'star_map.preset.handwritten_memory',
        theme: 2,
        colors: { background: 'cream', sky: 'navy', stars: 'cream', lines: 'gold', text: 'navy' },
        fonts: 'handwritten',
    },
    {
        name: 'Hajnali rózsa', labelKey: 'star_map.preset.dawn_rose',
        theme: 2,
        colors: { background: 'powder', stars: 'burgundy', lines: 'gold', text: 'burgundy' },
        fonts: 'dreamy',
    },
    {
        name: 'Űrutazás', labelKey: 'star_map.preset.space_travel',
        theme: 1,
        colors: { background: 'midnight', sky: 'black', stars: 'white', lines: 'blue', text: 'white' },
        fonts: 'futuristic',
    },
    {
        name: 'Napfényes retro', labelKey: 'star_map.preset.sunny_retro',
        theme: 2,
        colors: { background: 'terracotta', sky: 'cream', stars: 'sepia', lines: 'sepia', text: 'cream' },
        fonts: 'retro',
    },
    {
        name: 'Szívünk egén', labelKey: 'star_map.preset.sky_of_our_hearts',
        theme: 2,
        shape: 3,
        colors: { background: 'powder', sky: 'burgundy', stars: 'cream', lines: 'gold', text: 'burgundy' },
        fonts: 'romantic',
    },
    {
        name: 'Hatszög éjszaka', labelKey: 'star_map.preset.hexagon_night',
        theme: 1,
        shape: 7,
        colors: { background: 'midnight', stars: 'white', lines: 'gold', text: 'white' },
        fonts: 'minimal',
    },
    {
        name: 'Rozettakert', labelKey: 'star_map.preset.rosette_garden',
        theme: 2,
        shape: 12,
        colors: { background: 'sage', sky: 'forest', stars: 'cream', lines: 'cream', text: 'forest' },
        fonts: 'editorial',
    },
    {
        name: 'Csillagkapu', labelKey: 'star_map.preset.stargate',
        theme: 1,
        shape: 5,
        colors: { background: 'black', sky: 'midnight', stars: 'gold', lines: 'gold', text: 'gold' },
        fonts: 'display',
    },
    {
        name: 'Nemesi címer', labelKey: 'star_map.preset.noble_crest',
        theme: 1,
        shape: 11,
        colors: { background: 'burgundy', sky: 'midnight', stars: 'cream', lines: 'gold', text: 'cream' },
        fonts: 'vintage',
    },
    {
        name: 'Polaroid emlék', labelKey: 'star_map.preset.polaroid_memory',
        theme: 2,
        shape: 1,
        colors: { background: 'paper', sky: 'navy', stars: 'white', lines: 'white', text: 'navy' },
        fonts: 'handwritten',
    },
];

// Final colours for a theme and the selected overrides ({ color } objects, `color: null` = automatic).
// `custom` means the artwork is replaced by solid background and sky colours. Cutouts (`shaped`) need
// that too: the artwork has a printed disc ring that the Keret switch now owns.
export function resolveStarMapColors(theme, { background, sky, stars, lines, text, shaped = false }) {
    const custom = Boolean(background?.color || sky?.color || shaped);
    const backgroundColor = background?.color || theme.background;
    // Themes without a visible disc keep it that way when only the background changes.
    const themeSky = sameColor(theme.sky, theme.background) ? backgroundColor : theme.sky;
    const skyColor = sky?.color || themeSky;
    const starColor = stars?.color || (custom ? contrastColor(skyColor) : theme.stars);
    return {
        custom,
        background: backgroundColor,
        sky: skyColor,
        stars: starColor,
        lines: lines?.color || starColor,
        text: text?.color || (custom ? contrastColor(backgroundColor) : theme.text),
    };
}

export function buildStarMapPresets(themes, backgrounds = STAR_MAP_BACKGROUNDS, colors = TEXT_COLORS) {
    return PRESET_LOOKS.map((look, index) => {
        const hex = (key) => COLORS[(look.colors || {})[key]];
        const theme = themes.find((option) => option.id === look.theme);
        const options = {
            theme: look.theme,
            background: matchColor(hex('background'), backgrounds),
            sky: matchColor(hex('sky') || hex('background'), backgrounds),
            starColor: matchColor(hex('stars'), colors),
            lineColor: matchColor(hex('lines'), colors),
            textColor: matchColor(hex('text'), colors),
            shape: { id: look.shape || CIRCLE_SHAPE_ID },
            ...fontPair(look.fonts),
        };
        const resolved = resolveStarMapColors(theme, {
            shaped: true,
            background: options.background,
            sky: options.sky,
            stars: options.starColor,
            lines: options.lineColor,
            text: options.textColor,
        });
        const preview = { backgroundColor: resolved.background, color: resolved.text };
        return { ...presetEntry(index + 1, look.name, options, preview), labelKey: look.labelKey };
    });
}

export function starMapPresetMatches(preset, selected) {
    const { options } = preset;
    return (
        options.theme === selected.theme?.id &&
        options.shape.id === (selected.shape?.id ?? CIRCLE_SHAPE_ID) &&
        ['background', 'sky', 'starColor', 'lineColor', 'textColor'].every((key) =>
            sameColor(options[key].color, selected[key]?.color)
        ) &&
        options.titleTypo?.label === selected.title?.typo?.label &&
        options.messageTypo?.label === selected.message?.typo?.label
    );
}
